import type { AxiosInstance, AxiosRequestConfig } from "axios";

type RequestConfigWithSignal = AxiosRequestConfig & { signal?: AbortSignal };

export const API_REQUEST_TIMEOUT_MS = 30_000;
export const ORG_SWITCH_PENDING_TIMEOUT_MS = 30_000;

let pendingCount = 0;
const listeners = new Set<() => void>();

let orgSwitchAbortController = new AbortController();

export const getPendingRequestCount = (): number => pendingCount;

export const hasPendingRequests = (): boolean => pendingCount > 0;

export const subscribePendingRequests = (listener: () => void): (() => void) => {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
};

const notify = (): void => {
  listeners.forEach((listener) => listener());
};

const adjustPendingCount = (delta: number): void => {
  pendingCount = Math.max(0, pendingCount + delta);
  notify();
};

/**
 * Aborts in-flight API calls and resets the signal used for subsequent requests.
 */
export const abortInFlightRequestsForOrgSwitch = (): void => {
  orgSwitchAbortController.abort();
  orgSwitchAbortController = new AbortController();
};

/**
 * Resolves when no tracked requests are pending, or after the timeout (whichever is first).
 * On timeout, the switch proceeds anyway — slow requests are left to finish or fail on their own.
 */
export const waitForPendingRequests = (
  timeoutMs = ORG_SWITCH_PENDING_TIMEOUT_MS,
): Promise<"idle" | "timeout"> => {
  if (!hasPendingRequests()) {
    return Promise.resolve("idle");
  }

  return new Promise((resolve) => {
    const timeoutId = window.setTimeout(() => {
      unsubscribe();
      resolve("timeout");
    }, timeoutMs);

    const unsubscribe = subscribePendingRequests(() => {
      if (!hasPendingRequests()) {
        window.clearTimeout(timeoutId);
        unsubscribe();
        resolve("idle");
      }
    });
  });
};

const attachOrgSwitchAbortSignal = (
  config: RequestConfigWithSignal,
): RequestConfigWithSignal => {
  const existingSignal = config.signal;

  if (!existingSignal) {
    config.signal = orgSwitchAbortController.signal;

    return config;
  }

  if (typeof AbortSignal !== "undefined" && "any" in AbortSignal) {
    config.signal = AbortSignal.any([
      existingSignal,
      orgSwitchAbortController.signal,
    ]);
  }

  return config;
};

const trackedInstances = new WeakSet<AxiosInstance>();

export const attachPendingRequestTracking = (axiosInstance: AxiosInstance): void => {
  if (trackedInstances.has(axiosInstance)) {
    return;
  }

  trackedInstances.add(axiosInstance);

  axiosInstance.interceptors.request.use((config) => {
    adjustPendingCount(1);
    return attachOrgSwitchAbortSignal(config);
  });

  axiosInstance.interceptors.response.use(
    (response) => {
      adjustPendingCount(-1);
      return response;
    },
    (error) => {
      adjustPendingCount(-1);
      return Promise.reject(error);
    },
  );
};
